import React from 'react';
import Tabs from 'src/components/Tabs';
import ServiceLog from 'src/pages/ServiceRequest/ServiceLog/ServiceLog';
import ServiceRequestDetails from 'src/pages/ServiceRequest/ServiceRequestDetails';
import ModernizedNotes from 'src/components/Notes/ModernizedNotes';
import ModernizedServiceRequestDocuments from 'src/pages/MagicLinkSignedOutServiceRequest/components/ModernizedServiceRequestDocuments/ModernizedServiceRequestDocuments';
import ModernMessages from 'src/components/Messages/ModernizedMessages/ModernMessages';
import LoadState from 'src/components/LoadState/LoadState';
import Skeleton from 'src/components/Skeleton/Skeleton';
import ServiceRequestDetailsSkeleton from 'src/pages/ServiceRequest/ServiceRequestDetails/skeletons/ServiceRequestDetailsSkeleton';
import {
  useServiceRequestTabsOrchestrator,
  UseServiceRequestTabsOrchestratorReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useServiceRequestTabsOrchestrator';

interface ServiceRequestTabsProps {
  user: any;
  requestId: string;
  activeTab: number;
  loadingSR?: boolean;
  serviceRequest: any;
  refreshData: () => Promise<void>;
  onNotePinned?: () => void;
  openNotesSignal?: number;
}

const ServiceRequestTabs = ({
  user,
  requestId,
  activeTab,
  loadingSR,
  serviceRequest,
  refreshData,
  onNotePinned,
  openNotesSignal,
}: ServiceRequestTabsProps) => {
  if (loadingSR) {
    const skeletonBadge = <Skeleton variant="text" width="1.4rem" />;

    const placeholderContent = (
      <LoadState isLoading={true}>
        <></>
      </LoadState>
    );

    const placeholderItems: any = [
      { label: 'Details', content: <ServiceRequestDetailsSkeleton /> },
      { label: 'Messages', count: skeletonBadge, content: placeholderContent },
      {
        label: 'Photos & Documents',
        count: skeletonBadge,
        content: placeholderContent,
      },
    ];

    return (
      <div className="serviceRequest__tabs">
        <Tabs from={undefined} activeTab={activeTab} items={placeholderItems} />
      </div>
    );
  }

  const {
    messages,
    isSending,
    documents,
    loadingDocs,
    notesAPI,
    canEditAsset,
    canAddNote,
    canDelete,
    isHomeOwnerRealtorOccupant,
    isPropertyManagerRole,
    handlePostMessage,
    handleSaveDocument,
    handleDeleteDocument,
  }: UseServiceRequestTabsOrchestratorReturn = useServiceRequestTabsOrchestrator(
    {
      serviceRequestId: requestId,
      user,
      serviceRequest,
    }
  );

  const { notes } = notesAPI;

  const tabItems: any = [
    {
      label: 'Details',
      content: (
        <ServiceRequestDetails
          serviceRequest={serviceRequest}
          canEditAsset={canEditAsset}
          refreshData={refreshData}
        />
      ),
    },
    {
      label: 'Messages',
      count: messages.length.toString(),
      content: (
        <ModernMessages
          messages={messages}
          saving={isSending}
          offlineUser={{
            id: user.id,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            roles: user.roles,
          }}
          onPostMessage={handlePostMessage}
        />
      ),
    },
    {
      label: 'Photos & Documents',
      count: documents.length.toString(),
      content: (
        <ModernizedServiceRequestDocuments
          documents={documents}
          userId={user.id}
          saving={loadingDocs}
          onAddDocument={handleSaveDocument}
          onDeleteDocument={handleDeleteDocument}
          user={user}
          canDelete={canDelete}
        />
      ),
    },
  ];

  const hasNotesVisibleToResidents: boolean = notes.some(
    (n: any) => n.noteVisibleToHomeownerOccupantRealtor === true
  );

  if (!(isHomeOwnerRealtorOccupant && !hasNotesVisibleToResidents)) {
    tabItems.push({
      label: 'Notes',
      count: notes.length.toString(),
      content: (
        <ModernizedNotes
          notesAPI={notesAPI}
          entityId={requestId}
          itemType="Service Request"
          serviceRequestId={requestId}
          serviceRequestNumber={serviceRequest?.ticketNumber}
          onNotePinned={onNotePinned}
          userId={user.id}
          canAddNote={canAddNote}
          canDelete={canDelete}
          loading={notesAPI.loadingNotes && notes.length === 0}
          addDialogDescription="Leave a status update or general comment for the team."
          editDialogDescription="Update your note below."
          showHomeownerVisibilityCheckbox
        />
      ),
    });
  }

  if (isPropertyManagerRole) {
    tabItems.push({
      label: 'Service Log',
      content: (
        <ServiceLog
          serviceRequestId={requestId}
          actualTime={serviceRequest.actualTime}
          estimatedTime={serviceRequest.estimatedTime}
        />
      ),
    });
  }

  const notesTabIndex: number = tabItems.findIndex(
    (tab: any) => tab?.label === 'Notes'
  );

  const shouldOpenNotes: boolean =
    Boolean(openNotesSignal) && notesTabIndex >= 0;

  const effectiveActiveTab: number = shouldOpenNotes
    ? notesTabIndex
    : activeTab;

  return (
    <div className="serviceRequest__tabs">
      <Tabs
        key={shouldOpenNotes ? `open-notes-${openNotesSignal}` : 'default-tabs'}
        from={undefined}
        activeTab={effectiveActiveTab}
        items={tabItems}
      />
    </div>
  );
};

export default ServiceRequestTabs;
